import PanelStore from 'stores/PanelStore'
import UserStore from 'stores/UserStore'
import localStorageUtils from 'utils/localStorageUtils'

// PanelStore/UserStore are untyped MobX JS singletons; describe only the slice we read and cast at
// the boundary so the logic below is type-checked (see use-electric for the same store-casting need).
type TPanelStore = { selectedPanel?: { panelId?: string } | false }
type TUserStore = {
  userInfo?: { brands?: Array<{ panels?: Array<{ id?: string }> } | null> }
  // `panelId` comes from the untyped routing store; treat it as unknown and narrow before use.
  urlParams?: { panelId?: unknown } | null
}

const panelStore = PanelStore as unknown as TPanelStore
const userStore = UserStore as unknown as TUserStore

// The `panelId` used as the `x-insights-workspace` header must belong to one of the user's active
// workspaces. The server's `@authorized` directive validates that header and rejects the *entire*
// request when it points at a workspace the user can't access. A `panelId` reaching this function
// from localStorage (persisted across sessions/users) or from a URL param can be stale or foreign,
// so validate candidates against the user's own workspaces and heal the poisoned entry.
const getUserPanelIds = (): string[] =>
  (userStore.userInfo?.brands ?? []).map(brand => brand?.panels?.[0]?.id).filter((id): id is string => Boolean(id))

const getCurrentWorkspace = (): string | null => {
  // An explicit in-app selection is always one of the user's panels by construction.
  const selectedPanel = panelStore.selectedPanel
  if (selectedPanel && selectedPanel.panelId) return selectedPanel.panelId

  const userPanelIds = getUserPanelIds()

  // Before the user is loaded we can't validate; leave candidates untouched (the auth bootstrap
  // query opts out of this header entirely, so an unvalidated value can't blank the app).
  const isAccessible = (panelId: string) => userPanelIds.length === 0 || userPanelIds.includes(panelId)

  // Candidates come from untyped sources (routing store, JSON.parse'd localStorage). Only a string
  // panelId is usable — anything else would be stringified into the header (e.g. "[object Object]").
  const urlPanelId = userStore.urlParams?.panelId
  if (typeof urlPanelId === 'string' && isAccessible(urlPanelId)) {
    localStorageUtils.setObject('panelId', urlPanelId)
    return urlPanelId
  }

  const storedPanelId = localStorageUtils.getObject('panelId')
  const panelSelectedLS = typeof storedPanelId === 'string' ? storedPanelId : undefined
  if (panelSelectedLS && isAccessible(panelSelectedLS)) {
    return panelSelectedLS
  }

  // The stored/URL panelId is foreign or absent: fall back to the user's first workspace and heal
  // the poisoned localStorage entry so subsequent requests stop sending an inaccessible workspace.
  const fallbackPanelId = userPanelIds[0]
  if (fallbackPanelId) {
    localStorageUtils.setObject('panelId', fallbackPanelId)
    return fallbackPanelId
  }

  return panelSelectedLS || null
}

export default getCurrentWorkspace
